import { Component } from '@angular/core';
import { Chart, registerables } from 'chart.js/auto';
import 'chartjs-plugin-datalabels';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { DatePipe } from '@angular/common';
import * as moment from 'moment';
import { LoginModel, Stores } from 'src/app/Models/ApplicationModels';
import { MasterDataSeriveService } from 'src/app/Services/master-data-serive.service';
import { AdminBreadcrumbsService } from 'src/app/rcs/admin-module/admin-services/admin-breadcrumbs.service';
import { OverrideStatus } from 'src/app/enum/enum';
import { ProductionCycle } from 'src/app/rcs/admin-module/company-config/WeekColors/models/week-color.model';
import { WeekColorService } from 'src/app/rcs/admin-module/company-config/WeekColors/services/week-color.service';
import { ReportsV3ApiService } from '../../services/reports-v3-api.service';
import { AdminDashboardDetailsV3Response } from '../models/production-overview-admin-v3-models';

@Component({
  selector: 'app-production-overview-admin-dashboard-v3',
  templateUrl: './production-overview-admin-dashboard-v3.component.html',
  styleUrls: ['./production-overview-admin-dashboard-v3.component.css'],
})
export class ProductionOverviewAdminDashboardV3Component {
  userInfo: LoginModel = new LoginModel();
  stores: Array<Stores> = [];
  storeId: number = 0;
  storeName: string = 'All stores';
  startDate: any;
  endDate: any;
  isLoading: boolean = false;
  reportHasRun: boolean = false;
  showWeekColor: boolean = true;
  weekColor: string = 'All Week Colors';
  weekColorsList: Array<ProductionCycle> = [];
  productionCycleId: number = 0;
  dashboardData: AdminDashboardDetailsV3Response | null = null;
  isNoDataFound: boolean = false;
  isDeptData: boolean = false;
  isEmpData: boolean = false;
  isBrandData: boolean = false;
  isStoreData: boolean = false;
  public daterange: any = {};
  private charts: any[] = [];

  public options: any = {
    locale: { format: 'MM/DD/YYYY', separator: ' - ', applyLabel: 'Apply', cancelLabel: 'Cancel' },
    alwaysShowCalendars: true,
    showDropdowns: false,
    showCustomRangeLabel: true,
    maxDate: new Date(),
    ranges: {
      Today: [moment(), moment()],
      Yesterday: [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
      'Last 7 Days': [moment().subtract(6, 'days'), moment()],
      'This Month': [moment().startOf('month'), moment().endOf('month')],
    },
    opens: 'left',
  };

  constructor(
    private datepipe: DatePipe,
    private masterservice: MasterDataSeriveService,
    private adminBreadcrumbs: AdminBreadcrumbsService,
    private weekColorService: WeekColorService,
    private reportsV3Service: ReportsV3ApiService
  ) { }

  ngOnInit() {
    this.adminBreadcrumbs.updateBreadcrumbs([
      { crumbName: 'Reports', link: 'demo' },
      { crumbName: 'Production Overview Admin', link: '/admin/home/v3/production-overview-admin' },
    ]);
    this.startDate = moment();
    this.endDate = moment();
    this.userInfo = JSON.parse(localStorage.getItem('user')!);
    const productionScheduleType = (this.userInfo as any)?.productionScheduleType?.toString()?.trim()?.toLowerCase();
    this.showWeekColor = productionScheduleType !== 'datespecific';
    this.getStores();
    if (this.showWeekColor) {
      this.getWeekColors();
    }
  }

  getStores() {
    let user = this.masterservice.getUserDetailsFromStorage();
    this.masterservice.getallstores(user.companyID).subscribe((res) => {
      if (Array.isArray(res)) {
        this.stores = res;
      }
    });
  }

  getWeekColors() {
    let user = this.masterservice.getUserDetailsFromStorage();
    this.weekColorService.getProductionCycles(user.companyID).subscribe((res) => {
      this.weekColorsList = (res as ProductionCycle[]).filter(
        (item) => item.status === OverrideStatus.Active
      );
    });
  }

  selectAllStores() {
    this.storeName = 'All stores';
    this.storeId = 0;
  }

  setStore(store: Stores) {
    this.storeName = store.storeName;
    this.storeId = store.storeId;
  }

  selectAllColors() {
    this.weekColor = 'All Week Colors';
    this.productionCycleId = 0;
  }

  setWeekColor(wk: ProductionCycle) {
    this.weekColor = wk.displayName;
    this.productionCycleId = wk.id;
  }

  public selectedDate(value: any, datepicker?: any) {
    this.endDate = value.end._d;
    this.startDate = value.start._d;
    if (datepicker) {
      datepicker.start = value.start;
      datepicker.end = value.end;
    }
    this.daterange.start = value.start;
    this.daterange.end = value.end;
    this.daterange.label = value.label;
  }

  runReport() {
    this.reportHasRun = true;
    this.destroyCharts();
    this.loadDashboard();
  }

  loadDashboard() {
    this.isLoading = true;
    const formattedStartDate = this.datepipe.transform(this.startDate, 'yyyy-MM-dd HH:mm:ss');
    const formattedEndDate = this.datepipe.transform(this.endDate, 'yyyy-MM-dd HH:mm:ss');
    this.reportsV3Service
      .getProductionOverviewAdminDashboardV3(
        this.userInfo.companyID,
        this.storeId,
        formattedStartDate,
        formattedEndDate,
        this.productionCycleId
      )
      .subscribe({
        next: (res: any) => {
          this.dashboardData = res as AdminDashboardDetailsV3Response;
          this.isDeptData = !this.dashboardData?.department?.length;
          this.isEmpData = !this.dashboardData?.emp?.length;
          this.isBrandData = !this.dashboardData?.brand?.length;
          this.isStoreData = !this.dashboardData?.store?.length;
          this.isNoDataFound = this.isDeptData && this.isEmpData && this.isBrandData;
          if (!this.isNoDataFound) {
            this.renderCharts();
          }
        },
        error: () => {
          this.isNoDataFound = true;
          this.isDeptData = true;
          this.isEmpData = true;
          this.isBrandData = true;
          this.isStoreData = true;
          this.isLoading = false;
        },
        complete: () => { this.isLoading = false; },
      });
  }

  renderCharts() {
    Chart.register(...registerables);
    Chart.register(ChartDataLabels);
    const barOpts: any = {
      plugins: {
        legend: { display: false },
        datalabels: { display: false },
      },
      layout: { padding: { bottom: 0 } },
      scales: {
        y: { beginAtZero: true, border: { display: false } },
        x: { border: { display: false }, grid: { display: false } },
      },
      responsive: true,
      maintainAspectRatio: false,
    };

    if (!this.isDeptData) {
      this.charts.push(new Chart('adminDeptV3', {
        type: 'bar',
        data: {
          labels: this.dashboardData!.department.map((d) => d.departmentDescr),
          datasets: [{
            label: 'Produced Qty',
            barThickness: 25,
            data: this.dashboardData!.department.map((d) => d.qty),
            backgroundColor: ['#95A4FC'],
            borderRadius: 8,
            borderSkipped: false,
          }],
        },
        options: barOpts,
      }));
    }

    if (!this.isEmpData) {
      this.charts.push(new Chart('adminEmpV3', {
        type: 'bar',
        data: {
          labels: this.dashboardData!.emp.map((e) => e.userName || '-'),
          datasets: [{
            label: 'Produced Qty',
            barThickness: 25,
            data: this.dashboardData!.emp.map((e) => e.qty),
            backgroundColor: ['#95A4FC'],
            borderRadius: 8,
            borderSkipped: false,
          }],
        },
        options: barOpts,
      }));
    }

    if (!this.isBrandData) {
      this.charts.push(new Chart('adminBrandV3', {
        type: 'bar',
        data: {
          labels: this.dashboardData!.brand.map((b) => b.brandDescr),
          datasets: [{
            label: 'Produced Qty',
            barThickness: 25,
            data: this.dashboardData!.brand.map((b) => b.qty),
            backgroundColor: ['#95A4FC'],
            borderRadius: 8,
            borderSkipped: false,
          }],
        },
        options: barOpts,
      }));
    }

    if (!this.isStoreData) {
      this.charts.push(new Chart('adminStoreV3', {
        type: 'doughnut',
        data: {
          labels: this.dashboardData!.store.map((s) => s.storeName),
          datasets: [{
            label: 'Produced Qty',
            data: this.dashboardData!.store.map((s) => s.qty),
            backgroundColor: Array.from({ length: this.dashboardData!.store.length }, () => this.getRandomColor()),
            borderRadius: 7,
            borderWidth: 3,
          }],
        },
        options: {
          cutout: '55%',
          plugins: {
            legend: { display: true, position: 'right', labels: { usePointStyle: true } },
            datalabels: { display: false },
          },
          layout: { padding: { bottom: 0, top: 10, left: 0, right: 40 } },
          scales: { x: { display: false }, y: { display: false } },
          maintainAspectRatio: false,
          responsive: true,
        },
      }));
    }
  }

  destroyCharts() {
    this.charts.forEach((c) => { if (c) c.destroy(); });
    this.charts = [];
  }

  getRandomColor() {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }
}
